import { Maybe } from "@flockcover/flock-svc-core-types"
import { Box, Flex, Text } from "@shared/primitive-components"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { SpeedBands } from "../../../api/insights"
import { getSpeedingBreakdownLineValues } from "../../../utils"
import { percentageLineStyles as styles } from "../../styles"

export const SPEEDING_BREAKDOWN_DTI = {
  LAWFUL_LINE: `lawful-line`,
  MINOR_LINE: `minor-line`,
  MAJOR_LINE: `major-line`,
  CRITICAL_LINE: `critical-line`,
  LAWFUL_SQUARE: `lawful-square`,
  MINOR_SQUARE: `minor-square`,
  MAJOR_SQUARE: `major-square`,
  CRITICAL_SQUARE: `critical-square`,
  LAWFUL_LABEL: `lawful-label`,
  MINOR_LABEL: `minor-label`,
  MAJOR_LABEL: `major-label`,
  CRITICAL_LABEL: `critical-label`,
}

export type SpeedingBreakdownProps = {
  speedBands: Maybe<SpeedBands>
  extended?: boolean
}

export const SpeedingBreakdown = ({ speedBands, extended }: SpeedingBreakdownProps) => {
  if (!speedBands) return null
  const lineValues = getSpeedingBreakdownLineValues(speedBands)
  if (!lineValues) return null

  return (
    <Box css={styles.fullWidth}>
      <Flex css={styles.marginBottom(2)}>
        <Box
          css={styles.criticalPercentageLine(lineValues, extended)}
          data-testid={SPEEDING_BREAKDOWN_DTI.CRITICAL_LINE}
        />
        <Box css={styles.majorPercentageLine(lineValues, extended)} data-testid={SPEEDING_BREAKDOWN_DTI.MAJOR_LINE} />
        <Box css={styles.minorPercentageLine(lineValues, extended)} data-testid={SPEEDING_BREAKDOWN_DTI.MINOR_LINE} />
        <Box css={styles.lawfulPercentageLine(lineValues, extended)} data-testid={SPEEDING_BREAKDOWN_DTI.LAWFUL_LINE} />
      </Flex>
      <Flex flexDirection={extended ? `column` : `row`}>
        <Flex alignItems="center">
          <Box
            css={styles.criticalPercentageLineLabel(!!speedBands.criticalSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.CRITICAL_SQUARE}
          />
          <Text
            use="caption"
            css={styles.labelText(!!speedBands.criticalSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.CRITICAL_LABEL}
          >
            {formatPercentage(speedBands.criticalSpeedBandPercentage, 1)}
            {` `}
            {extended && <span>Critical (potential court summons)</span>}
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Box
            css={styles.majorPercentageLineLabel(!!speedBands.majorSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.MAJOR_SQUARE}
          />
          <Text
            use="caption"
            css={styles.labelText(!!speedBands.majorSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.MAJOR_LABEL}
          >
            {formatPercentage(speedBands.majorSpeedBandPercentage, 1)}
            {` `}
            {extended && <span>Major (potential points penalty)</span>}
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Box
            css={styles.minorPercentageLineLabel(!!speedBands.minorSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.MINOR_SQUARE}
          />
          <Text
            use="caption"
            css={styles.labelText(!!speedBands.minorSpeedBandPercentage)}
            data-testid={SPEEDING_BREAKDOWN_DTI.MINOR_LABEL}
          >
            {formatPercentage(speedBands.minorSpeedBandPercentage, 1)}
            {` `}
            {extended && <span>Minor (potential fine)</span>}
          </Text>
        </Flex>
        {extended && (
          <Flex alignItems="center">
            <Box
              css={styles.lawfulPercentageLineLabel(!!lineValues.lawfulSpeedBandPercentage)}
              data-testid={SPEEDING_BREAKDOWN_DTI.LAWFUL_SQUARE}
            />
            <Text
              use="caption"
              css={styles.labelText(!!lineValues.lawfulSpeedBandPercentage)}
              data-testid={SPEEDING_BREAKDOWN_DTI.LAWFUL_LABEL}
            >
              {formatPercentage(lineValues.lawfulSpeedBandPercentage, 1)}
              {` `}
              <span>Safe driving</span>
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

SpeedingBreakdown.defaultProps = {
  extended: false,
}
